import path from "node:path";

import compression from "compression";
import cors from "cors";
import express, { Application, NextFunction, Request, Response } from "express";
import helmet from "helmet";
import mongoose from "mongoose";
import multer from "multer";

// Import controllers
import { schoolController } from "./controllers/school-controller";
// Import routes
import adminRoutes from "./routes/admin-routes";
import alertRoutes from "./routes/alert-routes";
import assignmentRoutes from "./routes/assignment-routes";
import assignmentStudentRoutes from "./routes/assignment-student-routes";
import attendanceRoutes from "./routes/attendance-routes";
import authRoutes from "./routes/auth-routes";
import breakTimerRoutes from "./routes/break-timer-routes";
import chatConversationRoutes from "./routes/chat-conversation-routes";
import chatRoutes from "./routes/chat-routes";
import classQuizReportRoutes from "./routes/class-quiz-report-routes";
import { classRoutes } from "./routes/class-routes";
import classWithdrawalRoutes from "./routes/class-withdrawal-routes";
import deviceRoutes from "./routes/device-routes";
import guestRoutes from "./routes/guest-routes";
import helpFeedbackRoutes from "./routes/help-feedback-routes";
import leaveRoutes from "./routes/leave-routes";
// Import mobile routes
import { studentMobileRoutes } from "./routes/mobile/student-mobile-routes";
import { teacherMobileRoutes } from "./routes/mobile/teacher-mobile-routes";
import notificationRoutes from "./routes/notification-routes";
import profileRoutes from "./routes/profile-routes";
import programAppealRoutes from "./routes/program-appeal-routes";
import quizRoutes from "./routes/quiz-routes";
import quizStudentRoutes from "./routes/quiz-student-routes";
import schoolRoutes from "./routes/school-routes";
import semesterEnrollmentRoutes from "./routes/semester-enrollment-routes";
import settingsRoutes from "./routes/settings-routes";
import studentDashboardRoutes from "./routes/student-dashboard-routes";
import { studentRoutes } from "./routes/student-routes";
import { studentSpecificRoutes } from "./routes/student-specific-routes";
import supportRoutes from "./routes/support-routes";
import { teacherRoutes } from "./routes/teacher-routes";
import teacherSpecificRoutes from "./routes/teacher-specific-routes";
import validationRoutes from "./routes/validation-routes";
import { fcmService } from "./services/fcm-service";
import { otpService } from "./services/otp-service";
// Import middleware and utilities
import { cleanupExpiredMediaFiles } from "./utils/file-cleanup";
import { httpLogger, logger } from "./utils/logger";

// Error handling interface
interface AppError extends Error {
  statusCode?: number;
  isOperational?: boolean;
}

class App {
  public app: Application;

  constructor() {
    this.app = express();
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.connectDB().then(() => {
      this.initializeFCM();
      this.initializeMiddleware();
      this.initializeRoutes();
      this.initializeErrorHandling();
      this.startBackgroundTasks();
    });
  }

  /**
   * Initialize Firebase Cloud Messaging
   */
  private initializeFCM(): void {
    try {
      fcmService.initialize();
      logger.info("FCM Service initialization attempted");
    } catch (error) {
      logger.error("FCM Service initialization error:", error);
      logger.warn("App will continue without push notification support");
    }
  }

  /**
   * Connect to MongoDB
   */
  private async connectDB(): Promise<void> {
    try {
      const mongoUri =
        process.env.MONGODB_URI ?? "mongodb://localhost:27017/covalent-backend";
      await mongoose.connect(mongoUri);
      logger.info("MongoDB connected successfully");
    } catch (error) {
      logger.error("MongoDB connection failed:", error);
      // process.exit(1);
    }
  }

  /**
   * Initialize middleware
   */
  private initializeMiddleware(): void {
    // Trust proxy for accurate IP addresses and protocol detection
    // In production with reverse proxy (cPanel), trust all proxies
    // This allows proper handling of X-Forwarded-* headers
    const trustProxy = process.env.TRUST_PROXY ?? "1";
    this.app.set("trust proxy", trustProxy === "true" || trustProxy === "1");

    // ---- CORS: MUST BE FIRST - Handle preflight OPTIONS requests immediately ----
    // Handle OPTIONS preflight requests BEFORE any other middleware
    this.app.use((req: Request, res: Response, next: NextFunction) => {
      // Set CORS headers for ALL requests (including preflight)
      // Echo the request Origin (if present) instead of always returning '*'.
      // This is required when clients send credentials (cookies/Authorization).
      const originHeader = req.get("Origin");
      res.setHeader("Access-Control-Allow-Origin", originHeader ?? "*");
      res.setHeader(
        "Access-Control-Allow-Methods",
        "GET, POST, PUT, DELETE, PATCH, OPTIONS, HEAD",
      );
      res.setHeader(
        "Access-Control-Allow-Headers",
        "Content-Type, Authorization, X-Requested-With, Accept, Origin, Access-Control-Request-Method, Access-Control-Request-Headers, x-api-key, x-device-id, x-app-version",
      );
      // If an Origin header is present, allow credentials. Otherwise keep false.
      res.setHeader(
        "Access-Control-Allow-Credentials",
        originHeader ? "true" : "false",
      );
      res.setHeader("Access-Control-Max-Age", "86400");

      // Handle preflight OPTIONS request immediately
      if (req.method === "OPTIONS") {
        logger.info(
          `CORS: Preflight request from ${req.headers.origin ?? "unknown"}`,
        );
        return res.status(204).end();
      }

      next();
    });

    // Use the cors library with a dynamic origin so we can support credentialed requests.
    this.app.use(
      cors({
        origin: (origin, callback) => {
          // Allow all origins by returning true here, but the middleware above
          // will echo the actual `Origin` header so browsers accept credentialed
          // responses. If you want to restrict origins, replace this with a
          // whitelist check.
          callback(null, true);
        },
        credentials: true,
        methods: ["GET", "POST", "PUT", "DELETE", "PATCH", "OPTIONS", "HEAD"],
        allowedHeaders: [
          "Content-Type",
          "Authorization",
          "X-Requested-With",
          "Accept",
          "Origin",
          "Access-Control-Request-Method",
          "Access-Control-Request-Headers",
          "x-api-key",
          "x-device-id",
          "x-app-version",
        ],
        exposedHeaders: [
          "Content-Length",
          "Content-Type",
          "Authorization",
          "X-Total-Count",
          "X-RateLimit-Limit",
          "X-RateLimit-Remaining",
          "X-RateLimit-Reset",
        ],
        optionsSuccessStatus: 204,
        maxAge: 86_400,
      }),
    );

    // Security middleware (keep after our CORS so it doesn't restrict)
    this.app.use(
      helmet({
        crossOriginResourcePolicy: { policy: "cross-origin" },
      }),
    );

    // Compression
    this.app.use(compression());

    // Body parsing middleware
    this.app.use(express.json({ limit: "10mb" }));
    this.app.use(express.urlencoded({ extended: true, limit: "10mb" }));

    // Static file serving for uploads
    this.app.use(
      "/uploads",
      express.static(path.join(process.cwd(), "uploads")),
    );

    // Enhanced request/response logging middleware
    this.app.use((req: Request, res: Response, next: NextFunction) => {
      const startTime = Date.now();
      const requestId = Math.random().toString(36).slice(2, 11);

      const originalSend = res.send;
      const originalJson = res.json;
      let responseBody: any = null;

      const requestLog = {
        requestId,
        timestamp: new Date().toISOString(),
        method: req.method,
        url: req.originalUrl,
        path: req.path,
        query: req.query,
        params: req.params,
        headers: {
          "content-type": req.get("Content-Type"),
          authorization: req.get("Authorization") ? "[REDACTED]" : undefined,
          "user-agent": req.get("User-Agent"),
          origin: req.get("Origin"),
        },
        body:
          req.method === "GET" ? undefined : this.sanitizeRequestBody(req.body),
        multipartFields: req.get("Content-Type")?.includes("multipart")
          ? this.extractMultipartFields(req)
          : undefined,
        files: (req as any).files
          ? this.getFileInfo((req as any).files)
          : undefined,
        ip: req.ip,
      };

      console.log("\n🟢 INCOMING REQUEST:");
      console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
      console.log(JSON.stringify(requestLog, null, 2));
      console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");

      res.send = function (body: any) {
        responseBody = body;
        return originalSend.call(this, body);
      };

      res.json = function (body: any) {
        responseBody = body;
        return originalJson.call(this, body);
      };

      res.on("finish", () => {
        const responseTime = Date.now() - startTime;
        const userId = (req as any).user?.userId ?? "anonymous";

        const responseLog = {
          requestId,
          timestamp: new Date().toISOString(),
          method: req.method,
          url: req.originalUrl,
          statusCode: res.statusCode,
          statusMessage: res.statusMessage,
          responseTime: `${responseTime}ms`,
          userId,
          responseBody: this.sanitizeResponseBody(responseBody),
          responseHeaders: {
            "content-type": res.get("Content-Type"),
            "content-length": res.get("Content-Length"),
            "access-control-allow-origin": res.get(
              "Access-Control-Allow-Origin",
            ),
          },
        };

        console.log("\n🔵 OUTGOING RESPONSE:");
        console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
        console.log(JSON.stringify(responseLog, null, 2));
        console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n");

        httpLogger.http("HTTP Request", {
          requestId,
          method: req.method,
          url: req.originalUrl,
          statusCode: res.statusCode,
          responseTime,
          userAgent: req.get("User-Agent"),
          userId,
          ip: req.ip,
        });
      });

      next();
    });

    logger.info("Middleware initialized successfully");
  }

  /**
   * Initialize routes
   */
  private initializeRoutes(): void {
    // API version prefix
    const API_PREFIX = "/api/v1";

    // Health check route (no prefix for simplicity)
    this.app.get("/health", (req: Request, res: Response) => {
      res.status(200).json({
        success: true,
        message: "Server is running",
        data: {
          status: "healthy",
          timestamp: new Date().toISOString(),
          version: process.env.APP_VERSION ?? "1.0.0",
          environment: process.env.NODE_ENV ?? "development",
          receivedOrigin: req.get("Origin") ?? null,
        },
      });
    });

    // Debug route
    this.app.get(`${API_PREFIX}/test`, (req: Request, res: Response) => {
      res.json({ message: "Test route works!" });
    });

    // Public routes (no authentication required)
    // Get all schools for dropdown - Public endpoints (both root and versioned)
    this.app.get("/schools", (req: Request, res: Response) => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      schoolController.getAllSchools(req, res);
    });
    this.app.get(`${API_PREFIX}/schools`, (req: Request, res: Response) => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      schoolController.getAllSchools(req, res);
    });
    // Get subjects for a school - Public (student profile creation / onboarding)
    this.app.get(
      `${API_PREFIX}/schools/:schoolId/subjects`,
      (req: Request, res: Response) => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        schoolController.getSchoolSubjects(req, res);
      },
    );

    // Authentication routes
    this.app.use(`${API_PREFIX}/auth`, authRoutes);

    // Admin routes (admin-only access to all data)
    this.app.use(`${API_PREFIX}/admin`, adminRoutes);

    // Guest routes (public school data access with guest token)
    this.app.use(`${API_PREFIX}/guest`, guestRoutes);

    // Get subjects for a school - Public (must be BEFORE school routes to avoid role check)
    this.app.get(
      `${API_PREFIX}/school/:schoolId/subjects`,
      (req: Request, res: Response) => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        schoolController.getSchoolSubjects(req, res);
      },
    );

    // Assignment routes (school/teacher) - must be before general /school routes
    this.app.use(`${API_PREFIX}/school/assignments`, assignmentRoutes);

    // Quiz routes (school/teacher) - must be before general /school routes
    this.app.use(`${API_PREFIX}/school/quizzes`, quizRoutes);

    // Class quiz report routes - must be before general /school routes
    this.app.use(`${API_PREFIX}/school/classes`, classQuizReportRoutes);

    // Semester enrollment routes (comprehensive semester management)
    this.app.use(`${API_PREFIX}/school/semesters`, semesterEnrollmentRoutes);

    // Teacher routes (teacher management for schools)
    this.app.use(`${API_PREFIX}/school/teachers`, teacherRoutes);

    // Student routes (student management for schools)
    this.app.use(`${API_PREFIX}/school/students`, studentRoutes);

    // School routes (semester and class management for schools)
    this.app.use(`${API_PREFIX}/school`, schoolRoutes);

    // Teacher-specific routes (for teachers to access their own data)
    this.app.use(`${API_PREFIX}/teacher`, teacherSpecificRoutes);

    // Student dashboard routes - must be before general /student routes
    this.app.use(`${API_PREFIX}/student/dashboard`, studentDashboardRoutes);

    // Assignment student routes (student assignment access and submission)
    this.app.use(`${API_PREFIX}/student/assignments`, assignmentStudentRoutes);

    // Quiz student routes (student quiz access and attempts)
    this.app.use(`${API_PREFIX}/student/quizzes`, quizStudentRoutes);

    // Student-specific routes (for students to access their own data)
    this.app.use(`${API_PREFIX}/student`, studentSpecificRoutes);

    // Class routes (comprehensive class management)
    this.app.use(`${API_PREFIX}/classes`, classRoutes);

    // Profile routes (profile creation and management)
    this.app.use(`${API_PREFIX}/profile`, profileRoutes);

    // Settings routes (user settings and preferences)
    this.app.use(`${API_PREFIX}/settings`, settingsRoutes);

    // Notification routes
    this.app.use(`${API_PREFIX}/notifications`, notificationRoutes);

    // Device routes (FCM token management)
    this.app.use(`${API_PREFIX}/devices`, deviceRoutes);

    // Support/Customer service routes
    this.app.use(`${API_PREFIX}/support`, supportRoutes);

    // Leave management routes
    this.app.use(API_PREFIX, leaveRoutes);

    // Class withdrawal request routes
    this.app.use(API_PREFIX, classWithdrawalRoutes);

    // Break timer routes
    this.app.use(API_PREFIX, breakTimerRoutes);

    // Chat routes
    this.app.use(API_PREFIX, chatRoutes);
    this.app.use(`${API_PREFIX}/chat`, chatConversationRoutes);

    // Program removal appeals
    this.app.use(API_PREFIX, programAppealRoutes);

    // Help & Feedback routes
    this.app.use(API_PREFIX, helpFeedbackRoutes);

    // Alert routes (Escort & Emergency)
    this.app.use(API_PREFIX, alertRoutes);

    // Attendance routes
    this.app.use(API_PREFIX, attendanceRoutes);

    // Validation routes (includes validate endpoints, health, and version)
    this.app.use(`${API_PREFIX}/validate`, validationRoutes);

    // Mobile routes (separate from school flows)
    this.app.use(`${API_PREFIX}/mobile/student`, studentMobileRoutes);
    this.app.use(`${API_PREFIX}/mobile/teacher`, teacherMobileRoutes);

    // API documentation route (placeholder)
    this.app.get(`${API_PREFIX}/docs`, (req: Request, res: Response) => {
      res.status(200).json({
        success: true,
        message: "API Documentation",
        data: {
          version: "v1",
          documentation:
            "https://documenter.getpostman.com/view/your-collection-id",
          endpoints: {
            authentication: `${API_PREFIX}/auth/*`,
            guest: `${API_PREFIX}/guest/*`,
            school: `${API_PREFIX}/school/*`,
            teachers: `${API_PREFIX}/school/teachers/*`,
            classes: `${API_PREFIX}/classes/*`,
            profiles: `${API_PREFIX}/profile/*`,
            validation: `${API_PREFIX}/validate/*`,
            mobile: {
              student: `${API_PREFIX}/mobile/student/*`,
              teacher: `${API_PREFIX}/mobile/teacher/*`,
            },
            health: "/health",
            version: `${API_PREFIX}/validate/version`,
          },
        },
      });
    });

    logger.info("Routes initialized successfully");

    // 404 handler for undefined routes (MUST be last)
    this.app.all("*", (req: Request, res: Response) => {
      res.status(404).json({
        success: false,
        message: `Route ${req.originalUrl} not found`,
        error: "Not Found",
      });
    });
  }

  /**
   * Initialize error handling
   */
  private initializeErrorHandling(): void {
    // Global error handler
    this.app.use(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      (error: AppError, req: Request, res: Response, next: NextFunction) => {
        logger.error("Global error handler:", {
          message: error.message,
          stack: error.stack,
          url: req.originalUrl,
          method: req.method,
          ip: req.ip,
          userAgent: req.get("User-Agent"),
        });

        // Mongoose validation error
        if (error.name === "ValidationError") {
          res.status(400).json({
            success: false,
            message: "Validation failed",
            error: error.message,
          });
          return;
        }

        // Mongoose duplicate key error
        if (
          error.name === "MongoServerError" &&
          "code" in error &&
          (error as any).code === 11_000
        ) {
          res.status(409).json({
            success: false,
            message: "Resource already exists",
            error: "Duplicate key error",
          });
          return;
        }

        // Multer errors (file upload)
        if (error instanceof multer.MulterError) {
          let status = 400;
          let message = "File upload error";

          switch (error.code) {
            case "LIMIT_FILE_SIZE": {
              status = 413;
              message =
                "File size too large. Maximum allowed size is 25MB for images and 50MB for documents.";
              break;
            }
            case "LIMIT_FILE_COUNT": {
              message =
                "Too many files uploaded. Please reduce the number of files.";
              break;
            }
            case "LIMIT_UNEXPECTED_FILE": {
              message = "Unexpected file field. Please check your form data.";
              break;
            }
            default: {
              message = `File upload error: ${error.message}`;
            }
          }

          res.status(status).json({
            success: false,
            message,
            error: error.message,
          });
          return;
        }

        // JWT errors
        if (error.name === "JsonWebTokenError") {
          res.status(401).json({
            success: false,
            message: "Invalid token",
            error: error.message,
          });
          return;
        }

        if (error.name === "TokenExpiredError") {
          res.status(401).json({
            success: false,
            message: "Token expired",
            error: error.message,
          });
          return;
        }

        // Default error response
        const statusCode = error.statusCode ?? 500;
        const message = error.isOperational
          ? error.message
          : "Internal server error. Please try again later.";

        res.status(statusCode).json({
          success: false,
          message,
          ...(process.env.NODE_ENV === "development" && {
            error: error.message,
            stack: error.stack,
          }),
        });
      },
    );

    // Handle uncaught exceptions
    process.on("uncaughtException", (error: Error) => {
      logger.error("Uncaught Exception:", error);
      process.exit(1);
    });

    // Handle unhandled promise rejections
    process.on("unhandledRejection", (reason: unknown) => {
      logger.error("Unhandled Rejection:", reason);
      process.exit(1);
    });

    logger.info("Error handling initialized successfully");
  }

  /**
   * Extract multipart form fields for logging
   */
  private extractMultipartFields(req: Request): any {
    const fields: any = {};
    if (req.body && Object.keys(req.body).length > 0) {
      Object.assign(fields, req.body);
    }
    if ((req as any).fields) {
      Object.assign(fields, (req as any).fields);
    }
    return Object.keys(fields).length > 0
      ? this.sanitizeRequestBody(fields)
      : "No form fields found";
  }

  /**
   * Get detailed file information for logging
   */
  private getFileInfo(files: any): any {
    if (!files) return undefined;
    const fileInfo: any = {};
    for (const [fieldName, fileArray] of Object.entries(files)) {
      if (Array.isArray(fileArray)) {
        fileInfo[fieldName] = fileArray.map(file => ({
          originalName: file.originalname,
          size: file.size,
          mimeType: file.mimetype,
        }));
      }
    }
    return fileInfo;
  }

  /**
   * Sanitize request body for logging (remove sensitive data)
   */
  private sanitizeRequestBody(body: any): any {
    if (!body || typeof body !== "object") return body;
    const sanitized = { ...body };
    const sensitiveFields = [
      "password",
      "token",
      "secret",
      "key",
      "auth",
      "authorization",
    ];
    for (const field of sensitiveFields) {
      if (sanitized[field]) sanitized[field] = "[REDACTED]";
    }
    return sanitized;
  }

  /**
   * Sanitize response body for logging (remove sensitive data)
   */
  private sanitizeResponseBody(body: any): any {
    if (!body) return body;
    try {
      const parsed = typeof body === "string" ? JSON.parse(body) : body;
      if (typeof parsed === "object" && parsed !== null) {
        const sanitized = { ...parsed };
        if (sanitized.data?.tokens) sanitized.data.tokens = "[REDACTED]";
        if (sanitized.data?.accessToken)
          sanitized.data.accessToken = "[REDACTED]";
        if (sanitized.data?.refreshToken)
          sanitized.data.refreshToken = "[REDACTED]";
        return sanitized;
      }
      return parsed;
    } catch {
      const str = String(body);
      return str.length > 1000 ? str.slice(0, 1000) + "...[TRUNCATED]" : str;
    }
  }

  /**
   * Start background tasks
   */
  private startBackgroundTasks(): void {
    const OTP_CLEANUP_INTERVAL = 60 * 60 * 1000; // 1 hour
    const FILE_CLEANUP_INTERVAL = 60 * 60 * 1000; // 1 hour

    // OTP cleanup task
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    setInterval(async () => {
      try {
        await otpService.cleanupExpiredOTPs();
      } catch (error) {
        logger.error("OTP cleanup error:", error);
      }
    }, OTP_CLEANUP_INTERVAL);

    // File cleanup task
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    setInterval(async () => {
      try {
        const deletedCount = await cleanupExpiredMediaFiles();
        if (deletedCount > 0) {
          logger.info(`File cleanup completed: ${deletedCount} files deleted`);
        }
      } catch (error) {
        logger.error("File cleanup error:", error);
      }
    }, FILE_CLEANUP_INTERVAL);

    logger.info(
      "Background tasks started successfully (OTP cleanup, File cleanup)",
    );
  }

  /**
   * Get the Express app instance
   * Server is now started in server.ts
   */
  public getApp(): Application {
    return this.app;
  }
}

export default App;
